import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { io, Socket } from "socket.io-client";
import SimplePeer from "simple-peer";
import {
  ChatContextType,
  ChatSession,
  ChatMessage,
  ChatUser,
  ChatUserStatus,
  VideoCallState,
} from "../types/chat";
import api from "../infra/api";
import popSound from "../assets/pop.mp3";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ChatNotification } from "../components/ChatNotification";
import ModalVideoCalling from "../components/ModalVideoCalling";
import ModalVideoIncoming from "../components/ModalVideoIncoming";
import ModalVideoCall from "../components/ModalVideoCall";
import { useAuth } from "../../auth/contexts/AuthProvider";

const ChatContext = createContext<ChatContextType | undefined>(undefined);

const VIDEO_CALL_VIDEO = {
  width: { ideal: 1280 },
  height: { ideal: 720 },
  frameRate: { ideal: 24 },
} as MediaTrackConstraints;

const VIDEO_CALL_AUDIO = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
} as MediaTrackConstraints;

const VIDEO_CALL_MAX_BITRATE = 1500000;

function getVideoCallConstraints(
  preferredAudioDevice: string | null,
  preferredVideoDevice: string | null,
): MediaStreamConstraints {
  return {
    video: {
      ...VIDEO_CALL_VIDEO,
      ...(preferredVideoDevice
        ? { deviceId: { ideal: preferredVideoDevice } }
        : {}),
    },
    audio: {
      ...VIDEO_CALL_AUDIO,
      ...(preferredAudioDevice
        ? { deviceId: { ideal: preferredAudioDevice } }
        : {}),
    },
  };
}

function applyVideoBitrate(peer: SimplePeer.Instance): void {
  try {
    const pc = (peer as any)._pc as RTCPeerConnection | undefined;
    if (!pc?.getSenders) return;
    const senders = pc.getSenders();
    const videoSender = senders.find(
      (s) => s.track?.kind === "video",
    );
    if (!videoSender) return;
    const params = videoSender.getParameters();
    if (!params.encodings?.length) {
      params.encodings = [{}];
    }
    params.encodings[0].maxBitrate = VIDEO_CALL_MAX_BITRATE;
    videoSender.setParameters(params).catch(() => {});
  } catch {}
}

interface ChatProviderProps {
  children: React.ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  console.log("🚀 ChatProvider renderizando...");
  const { authKey } = useAuth();

  const [currentUser, setCurrentUser] = useState<ChatUser | null>(null);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSession, setActiveSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<ChatUserStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [openChatModal, setOpenChatModal] = useState<
    ((session: ChatSession) => void) | null
  >(null);
  const [pendingSessionToOpen, setPendingSessionToOpen] =
    useState<ChatSession | null>(null);

  // Estado da chamada de vídeo
  const [videoCallState, setVideoCallState] = useState<VideoCallState | null>(
    null,
  );
  const [remoteStreamsVersion, setRemoteStreamsVersion] = useState(0); // Para forçar re-render quando streams mudam

  // Refs para manter referências atualizadas nos event handlers
  const activeSessionRef = useRef<ChatSession | null>(null);
  const messagesRef = useRef<ChatMessage[]>([]);
  const sessionsRef = useRef<ChatSession[]>([]);
  const loadUserSessionsRef = useRef<(() => Promise<void>) | null>(null);
  const openChatModalRef = useRef<((session: ChatSession) => void) | null>(
    null,
  );
  const isInitialized = useRef(false);
  const processedMessageIds = useRef<Map<number, number>>(new Map()); // Map<messageId, timestamp>
  const processingMessageIds = useRef<Set<number>>(new Set()); // Set para bloqueio imediato (race condition)

  // Refs para chamadas de vídeo (SimplePeer)
  const peerConnectionsRef = useRef<Map<number, SimplePeer.Instance>>(
    new Map(),
  );
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamsRef = useRef<Map<number, MediaStream>>(new Map());

  // Refs para funções de video call (evitar re-renderizações do useEffect)
  const createPeerConnectionRef = useRef<any>(null);
  const endVideoCallRef = useRef<any>(null);
  const playNotificationSoundRef = useRef<any>(null);

  // Atualizar refs quando os estados mudarem
  useEffect(() => {
    activeSessionRef.current = activeSession;
  }, [activeSession]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);

  useEffect(() => {
    openChatModalRef.current = openChatModal;
    console.log(
      "🔄 ChatProvider: openChatModalRef atualizado:",
      openChatModal ? "✅ definido" : "❌ null",
    );
    console.log("   Tipo:", typeof openChatModal);
    console.log("   Função?", typeof openChatModal === "function");
  }, [openChatModal]);

  // Wrapper para setOpenChatModal com logs
  const setOpenChatModalWithLogs = useCallback(
    (callback: ((session: ChatSession) => void) | null) => {
      console.log("📝 ChatProvider: setOpenChatModal CHAMADO!");
      console.log(
        "   Recebendo callback:",
        callback ? "✅ função definida" : "❌ null",
      );
      console.log("   Tipo:", typeof callback);
      setOpenChatModal(callback);
    },
    [],
  );

  // Função para reproduzir som de notificação
  const playNotificationSound = useCallback(() => {
    try {
      console.log("🔊 Tentando reproduzir som de notificação...");

      const audio = new Audio(popSound);
      audio.volume = 0.5; // Volume moderado
      audio
        .play()
        .then(() => {
          console.log("✅ Som reproduzido com sucesso");
        })
        .catch((error) => {
          console.error("❌ Erro ao reproduzir som de notificação:", error);
        });
    } catch (error) {
      console.error("❌ Erro ao criar áudio:", error);
    }
  }, []);

  // ============= FUNÇÕES DE CHAMADA DE VÍDEO =============

  // Iniciar chamada de vídeo
  const startVideoCall = useCallback(
    async (sessionId: number) => {
      if (!socket || !currentUser) {
        console.error("Socket ou usuário não disponível");
        return;
      }

      try {
        console.log("📹 Solicitando permissões de mídia...");

        // Buscar preferências de dispositivos salvas
        const preferredAudioDevice = localStorage.getItem(
          "preferredAudioDevice",
        );
        const preferredVideoDevice = localStorage.getItem(
          "preferredVideoDevice",
        );

        const constraints = getVideoCallConstraints(
          preferredAudioDevice,
          preferredVideoDevice,
        );
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia(constraints);
          if (preferredAudioDevice)
            console.log("   Usando microfone preferido:", preferredAudioDevice);
          if (preferredVideoDevice)
            console.log("   Usando câmera preferida:", preferredVideoDevice);
        } catch (error) {
          console.warn(
            "⚠️ Erro ao usar dispositivos preferidos, usando padrão:",
            error,
          );
          stream = await navigator.mediaDevices.getUserMedia({
            video: VIDEO_CALL_VIDEO,
            audio: VIDEO_CALL_AUDIO,
          });
        }

        localStreamRef.current = stream;
        console.log("✅ Stream local capturado!");
        console.log("   Tracks de vídeo:", stream.getVideoTracks().length);
        console.log("   Tracks de áudio:", stream.getAudioTracks().length);

        const callId = `call_${Date.now()}_${Math.random()}`;
        console.log("📞 Iniciando chamada:", callId);

        setVideoCallState({
          status: "calling" as const,
          callId,
          sessionId,
          participants: [],
          initiator: currentUser,
        });

        socket.emit("start_video_call", {
          sessionId,
          callId,
          callerId: currentUser.id,
        });
      } catch (error) {
        console.error("❌ Erro ao acessar mídia:", error);
        alert(
          "Não foi possível acessar sua câmera e microfone. Verifique as permissões do navegador.",
        );
      }
    },
    [socket, currentUser],
  );

  // Aceitar chamada de vídeo
  const acceptVideoCall = useCallback(
    async (callId: string) => {
      console.log("✅ ========================================");
      console.log("✅ acceptVideoCall CHAMADO");
      console.log("   Call ID:", callId);
      console.log("   Current user ID:", currentUser?.id);
      console.log("✅ ========================================");

      if (!socket || !currentUser) {
        console.error("❌ Socket ou usuário não disponível");
        return;
      }

      try {
        console.log("📹 Solicitando permissões de mídia...");

        // Buscar preferências de dispositivos salvas
        const preferredAudioDevice = localStorage.getItem(
          "preferredAudioDevice",
        );
        const preferredVideoDevice = localStorage.getItem(
          "preferredVideoDevice",
        );

        const constraints = getVideoCallConstraints(
          preferredAudioDevice,
          preferredVideoDevice,
        );
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia(constraints);
          if (preferredAudioDevice)
            console.log("   Usando microfone preferido:", preferredAudioDevice);
          if (preferredVideoDevice)
            console.log("   Usando câmera preferida:", preferredVideoDevice);
        } catch (error) {
          console.warn(
            "⚠️ Erro ao usar dispositivos preferidos, usando padrão:",
            error,
          );
          stream = await navigator.mediaDevices.getUserMedia({
            video: VIDEO_CALL_VIDEO,
            audio: VIDEO_CALL_AUDIO,
          });
        }

        localStreamRef.current = stream;
        console.log("✅ Stream local obtido com sucesso!");
        console.log("   Tracks de vídeo:", stream.getVideoTracks().length);
        console.log("   Tracks de áudio:", stream.getAudioTracks().length);

        console.log("📤 Emitindo evento accept_video_call para backend");
        socket.emit("accept_video_call", {
          callId,
          userId: currentUser.id,
        });
        console.log("✅ Evento accept_video_call emitido");
      } catch (error) {
        console.error("❌ Erro ao acessar mídia:", error);
        alert(
          "Não foi possível acessar sua câmera e microfone. Verifique as permissões do navegador.",
        );
      }
    },
    [socket, currentUser],
  );

  // Recusar chamada de vídeo
  const rejectVideoCall = useCallback(
    (callId: string) => {
      if (!socket || !currentUser) return;

      console.log("❌ Recusando chamada:", callId);

      socket.emit("reject_video_call", {
        callId,
        userId: currentUser.id,
      });

      setVideoCallState(null);
    },
    [socket, currentUser],
  );

  // Encerrar chamada de vídeo
  const endVideoCall = useCallback(() => {
    console.log("🔴 Encerrando/Saindo da chamada");

    // Fechar todas as conexões peer
    peerConnectionsRef.current.forEach((peer) => {
      peer.destroy();
    });
    peerConnectionsRef.current.clear();

    // Parar stream local
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }

    // Limpar streams remotos
    remoteStreamsRef.current.clear();

    // Notificar o servidor
    if (socket && videoCallState?.callId && currentUser) {
      // Número total de participantes na chamada (incluindo o usuário atual)
      const totalParticipants = videoCallState.participants.length;
      
      console.log(`📊 Total de participantes na chamada: ${totalParticipants}`);
      
      // Se houver 3 ou mais participantes, apenas sair sem encerrar para todos
      // Se houver 2 participantes (você + 1), encerrar para todos
      if (totalParticipants >= 3) {
        console.log("👋 Saindo da chamada (outros participantes continuarão)");
        socket.emit("leave_video_call", {
          callId: videoCallState.callId,
          userId: currentUser.id,
        });
      } else {
        console.log("🔴 Encerrando chamada para todos (2 ou menos participantes)");
        socket.emit("end_video_call", {
          callId: videoCallState.callId,
        });
      }
    }

    setVideoCallState(null);
  }, [socket, videoCallState, currentUser]);

  // Toggle mute/unmute
  const toggleAudio = useCallback(() => {
    const audioTrack = localStreamRef.current?.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.enabled = !audioTrack.enabled;
      console.log(
        audioTrack.enabled ? "🔊 Microfone ligado" : "🔇 Microfone mutado",
      );
    }
  }, []);

  // Toggle câmera
  const toggleVideo = useCallback(() => {
    const videoTrack = localStreamRef.current?.getVideoTracks()[0];
    if (videoTrack) {
      videoTrack.enabled = !videoTrack.enabled;
      console.log(
        videoTrack.enabled ? "📹 Câmera ligada" : "📷 Câmera desligada",
      );
    }
  }, []);

  // Trocar dispositivo de áudio
  const changeAudioDevice = useCallback(async (deviceId: string) => {
    try {
      console.log("🎤 Trocando microfone para:", deviceId);

      // Capturar novo stream com o dispositivo selecionado
      const newStream = await navigator.mediaDevices.getUserMedia({
        audio: { ...VIDEO_CALL_AUDIO, deviceId: { exact: deviceId } },
        video: false,
      });

      const newAudioTrack = newStream.getAudioTracks()[0];
      const oldAudioTrack = localStreamRef.current?.getAudioTracks()[0];

      if (localStreamRef.current && oldAudioTrack) {
        // Substituir track de áudio no stream local
        localStreamRef.current.removeTrack(oldAudioTrack);
        localStreamRef.current.addTrack(newAudioTrack);
        oldAudioTrack.stop();

        // Atualizar todos os peers com o novo track
        peerConnectionsRef.current.forEach((peer) => {
          // @ts-ignore - SimplePeer não tem types perfeitos
          const sender = peer._pc
            ?.getSenders?.()
            ?.find((s: any) => s.track?.kind === "audio");
          if (sender) {
            sender.replaceTrack(newAudioTrack);
          }
        });

        console.log("✅ Microfone trocado com sucesso!");
      }
    } catch (error) {
      console.error("❌ Erro ao trocar microfone:", error);
      alert("Não foi possível trocar o microfone. Verifique as permissões.");
    }
  }, []);

  // Trocar dispositivo de vídeo
  const changeVideoDevice = useCallback(async (deviceId: string) => {
    try {
      console.log("📹 Trocando câmera para:", deviceId);

      // Capturar novo stream com o dispositivo selecionado
      const newStream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { ...VIDEO_CALL_VIDEO, deviceId: { exact: deviceId } },
      });

      const newVideoTrack = newStream.getVideoTracks()[0];
      const oldVideoTrack = localStreamRef.current?.getVideoTracks()[0];

      if (localStreamRef.current && oldVideoTrack) {
        // Substituir track de vídeo no stream local
        localStreamRef.current.removeTrack(oldVideoTrack);
        localStreamRef.current.addTrack(newVideoTrack);
        oldVideoTrack.stop();

        // Atualizar todos os peers com o novo track
        peerConnectionsRef.current.forEach((peer) => {
          // @ts-ignore - SimplePeer não tem types perfeitos
          const sender = peer._pc
            ?.getSenders?.()
            ?.find((s: any) => s.track?.kind === "video");
          if (sender) {
            sender.replaceTrack(newVideoTrack);
          }
        });

        console.log("✅ Câmera trocada com sucesso!");

        // Forçar re-render para atualizar o preview local
        setRemoteStreamsVersion((v) => v + 1);
      }
    } catch (error) {
      console.error("❌ Erro ao trocar câmera:", error);
      alert("Não foi possível trocar a câmera. Verifique as permissões.");
    }
  }, []);

  // Criar conexão peer-to-peer com outro participante
  const createPeerConnection = useCallback(
    (userId: number, callId: string, initiator: boolean) => {
      if (!socket || !currentUser) {
        console.error("❌ Socket ou usuário não disponível");
        return;
      }

      // Se já existe um peer para este usuário, não criar outro
      if (peerConnectionsRef.current.has(userId)) {
        console.log(`⚠️ Peer com usuário ${userId} já existe, pulando criação`);
        return;
      }

      console.log("🔗 ========================================");
      console.log(`🔗 Criando conexão peer com usuário ${userId}`);
      console.log("   Initiator:", initiator);
      console.log("   Call ID:", callId);
      console.log("   Current user ID:", currentUser.id);
      console.log("   Tem stream local?", !!localStreamRef.current);
      console.log("🔗 ========================================");

      const peer = new SimplePeer({
        initiator,
        stream: localStreamRef.current || undefined,
        trickle: true,
        config: {
          iceServers: [
            { urls: "stun:stun.l.google.com:19302" },
            { urls: "stun:stun1.l.google.com:19302" },
          ],
        },
      });

      // Quando houver sinal (ICE candidate) para enviar
      peer.on("signal", (signal) => {
        console.log("📡 ========================================");
        console.log(`📡 Peer signal gerado para usuário ${userId}`);
        console.log("   Signal type:", signal.type);
        console.log("   Enviando para backend...");
        console.log("📡 ========================================");

        socket.emit("video_call_signal", {
          callId,
          to: userId,
          from: currentUser.id,
          signal,
        });
      });

      // Quando receber stream do participante
      peer.on("stream", (stream) => {
        console.log(`📹 Stream recebido do usuário ${userId}`);
        console.log("   Tracks de vídeo:", stream.getVideoTracks().length);
        console.log("   Tracks de áudio:", stream.getAudioTracks().length);

        // Adicionar stream ao Map
        remoteStreamsRef.current.set(userId, stream);

        // Incrementar version para forçar re-render
        setRemoteStreamsVersion((v) => {
          console.log(`   🔄 Incrementando version: ${v} -> ${v + 1}`);
          return v + 1;
        });
      });

      peer.on("connect", () => {
        console.log(`✅ Conectado com usuário ${userId}`);
        applyVideoBitrate(peer);
      });

      // Em caso de erro
      peer.on("error", (err) => {
        console.error(`❌ Erro na conexão peer com usuário ${userId}:`, err);
      });

      // Quando a conexão fechar
      peer.on("close", () => {
        console.log(`🔴 Conexão fechada com usuário ${userId}`);
        remoteStreamsRef.current.delete(userId);
        peerConnectionsRef.current.delete(userId);
      });

      // Armazenar a conexão
      peerConnectionsRef.current.set(userId, peer);
    },
    [socket, currentUser],
  );

  // ============= FIM DAS FUNÇÕES DE CHAMADA DE VÍDEO =============

  // Atualizar refs de funções de video call
  useEffect(() => {
    playNotificationSoundRef.current = playNotificationSound;
  }, [playNotificationSound]);

  useEffect(() => {
    createPeerConnectionRef.current = createPeerConnection;
  }, [createPeerConnection]);

  useEffect(() => {
    endVideoCallRef.current = endVideoCall;
  }, [endVideoCall]);

  useEffect(() => {
    if (!authKey) {
      setCurrentUser(null);
      setSessions([]);
      setMessages([]);
      setActiveSession(null);
      return;
    }
    const userId = localStorage.getItem("rh-userId");
    const userName = localStorage.getItem("rh-userName");
    const userEmail = localStorage.getItem("rh-email");
    if (userId) {
      const userData: ChatUser = {
        id: parseInt(userId),
        codigo: userId,
        nome: userName || "",
        nomecomp: userName || "",
        email: userEmail || "",
        ativo: 1,
      };
      setCurrentUser(userData);
    }
  }, [authKey]);

  useEffect(() => {
    if (!authKey) {
      setSocket(null);
      return;
    }
    const token = localStorage.getItem("rh-token");
    if (!token) return;

    const socketUrl =
      process.env.REACT_APP_SOCKET_URL || "http://localhost:8881";
    console.log("Conectando ao WebSocket em:", socketUrl);
    console.log("Token:", token ? "presente" : "ausente");

    const newSocket = io(socketUrl, {
      auth: {
        token: token,
      },
    });

    newSocket.on("connect", () => {
      console.log("Conectado ao WebSocket - Socket ID:", newSocket.id);

      const userId = localStorage.getItem("rh-userId");
      const userName = localStorage.getItem("rh-userName");

      if (userId) {
        newSocket.emit("newUser", userId, userName, userId);
        console.log("Usuário registrado:", userId);
      }
    });

    newSocket.on("message_read", ({ messageId, userId }) => {
      setMessages((prev) =>
        prev.map((message) =>
          message.id === messageId
            ? {
                ...message,
                reads: [
                  ...(message.reads || []),
                  {
                    id: Date.now(),
                    message_id: messageId,
                    user_id: userId,
                    read_at: new Date().toISOString(),
                  },
                ],
              }
            : message,
        ),
      );
    });

    newSocket.on("user_typing", ({ userId, sessionId }) => {
      console.log(`Usuário ${userId} está digitando na sessão ${sessionId}`);
    });

    newSocket.on("user_stopped_typing", ({ userId, sessionId }) => {
      console.log(`Usuário ${userId} parou de digitar na sessão ${sessionId}`);
    });

    newSocket.on("chat_error", (errorData) => {
      setError(errorData.message);
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, [authKey]);

  // Configurar eventos do WebSocket quando socket estiver pronto
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (message: ChatMessage) => {
      console.log("📨 New message received via WebSocket:");
      console.log("   Message ID:", message.id);
      console.log("   Type:", message.message_type);
      console.log("   Content:", message.message_content);
      console.log("   File URL:", (message as any).file_url);
      
      if ((message as any).attachments && typeof (message as any).attachments === 'string') {
        try {
          (message as any).attachments = JSON.parse((message as any).attachments);
        } catch (e) {
          console.error('Erro ao parsear attachments:', e);
          (message as any).attachments = null;
        }
      }

      const now = Date.now();

      // BLOQUEIO ULTRA-AGRESSIVO - Verificar TIMESTAMP recente (última camada, mais confiável)
      const lastProcessedTime = processedMessageIds.current.get(message.id);

      if (lastProcessedTime) {
        const timeSinceProcessed = now - lastProcessedTime;

        if (timeSinceProcessed < 100) {
          return;
        }
        if (timeSinceProcessed < 5000) {
          return;
        }
      }

      if (processingMessageIds.current.has(message.id)) {
        return;
      }

      processingMessageIds.current.add(message.id);
      processedMessageIds.current.set(message.id, now);

      setTimeout(() => {
        processingMessageIds.current.delete(message.id);
      }, 5000);

      if (processedMessageIds.current.size > 50) {
        const entriesToDelete: number[] = [];
        processedMessageIds.current.forEach((timestamp, id) => {
          if (now - timestamp > 60000) {
            entriesToDelete.push(id);
          }
        });
        entriesToDelete.forEach((id) => processedMessageIds.current.delete(id));
      }

      if (
        activeSessionRef.current &&
        message.session_id === activeSessionRef.current.id
      ) {
        setMessages((prev) => {
          // PRIMEIRA VERIFICAÇÃO: Se já existe por ID real (mais confiável)
          const existsByIdIndex = prev.findIndex(
            (msg) => msg.id === message.id,
          );
          if (existsByIdIndex !== -1) {
            console.log(
              "⚠️ DUPLICATA DETECTADA - Mensagem com ID já existe:",
              message.id,
            );
            return prev; // NÃO atualizar, apenas ignorar duplicata
          }

          // SEGUNDA VERIFICAÇÃO: Procurar mensagem temporária para substituir
          // Mensagem temporária = mesmo sender + mesmo conteúdo
          console.log("🔍 Procurando mensagem temporária para substituir...");
          console.log("Mensagem recebida:", {
            id: message.id,
            sender: message.sender_id,
            content: message.message_content.substring(0, 30),
          });

          const tempMessageIndex = prev.findIndex((msg) => {
            const isTempId = msg.id > 1000000000000; // IDs temporários são timestamps
            const sameSender = msg.sender_id === message.sender_id;
            const sameContent =
              msg.message_content.trim() === message.message_content.trim();

            if (isTempId && sameSender) {
              console.log("📝 Comparando com temporária:", {
                tempId: msg.id,
                tempContent: msg.message_content.substring(0, 30),
                sameContent,
                msgContent: msg.message_content,
                receivedContent: message.message_content,
              });
            }

            const isTemp = isTempId && sameSender && sameContent;

            if (isTemp) {
              console.log(
                "✅ MATCH! Encontrada mensagem temporária para substituir:",
                {
                  tempId: msg.id,
                  realId: message.id,
                },
              );
            }

            return isTemp;
          });

          console.log("Resultado da busca: índice =", tempMessageIndex);

          if (tempMessageIndex !== -1) {
            console.log(
              "🔄 Substituindo temporária (índice",
              tempMessageIndex,
              ") pela real (ID",
              message.id + ")",
            );
            const newMessages = [...prev];
            newMessages[tempMessageIndex] = message;
            return newMessages;
          }

          console.log("➕ Adicionando nova mensagem (ID:", message.id + ")");
          
          if (message.message_type === "audio") {
            console.log("🎵 Audio message details:");
            console.log("   Content (URL):", message.message_content);
            console.log("   File URL:", (message as any).file_url);
          }
          
          const newMessages = [...prev, message];
          console.log("✅ Total de mensagens agora:", newMessages.length);
          return newMessages;
        });
      } else {
        console.log("⏭️ Mensagem ignorada - sessão diferente ou não ativa");
      }

      // Atualizar última mensagem na sessão e contador de não lidas
      const currentUserId = parseInt(localStorage.getItem("rh-userId") || "0");
      const isOwnMessage = message.sender_id === currentUserId;
      const isActiveSession =
        activeSessionRef.current?.id === message.session_id;

      console.log("📊 Debug - Nova mensagem:", {
        messageId: message.id,
        sessionId: message.session_id,
        senderId: message.sender_id,
        currentUserId,
        isOwnMessage,
        isActiveSession,
        activeSessionId: activeSessionRef.current?.id,
        sessionType: sessionsRef.current.find(
          (s) => s.id === message.session_id,
        )?.session_type,
      });

      // Verificar se a sessão existe na lista
      const sessionExists = sessionsRef.current.some(
        (session) => session.id === message.session_id,
      );

      if (!sessionExists) {
        // Se a sessão não existe, recarregar lista de sessões
        console.log("⚠️ Sessão não encontrada na lista, recarregando sessões");
        if (loadUserSessionsRef.current) {
          loadUserSessionsRef.current();
        }
      } else {
        // Atualizar sessão existente
        setSessions((prev) =>
          prev.map((session) => {
            if (session.id === message.session_id) {
              // Incrementar unread_count se:
              // 1. Não é a sessão ativa (usuário não está vendo ou modal fechado)
              // 2. E a mensagem não é do próprio usuário
              const shouldIncrement = !isActiveSession && !isOwnMessage;

              console.log("📈 Atualizando contador:", {
                sessionId: session.id,
                sessionName: session.session_name,
                currentCount: session.unread_count ?? 0,
                shouldIncrement,
                newCount: shouldIncrement
                  ? (session.unread_count ?? 0) + 1
                  : (session.unread_count ?? 0),
              });

              return {
                ...session,
                last_message_at: message.sent_at,
                unread_count: shouldIncrement
                  ? (session.unread_count ?? 0) + 1
                  : (session.unread_count ?? 0),
              };
            }
            return session;
          }),
        );
      }

      // Reproduzir som de notificação se:
      // 1. Mensagem não é do próprio usuário
      // 2. E (modal está fechado OU mensagem é de outra sessão)
      const shouldPlaySound = !isOwnMessage && !isActiveSession;

      console.log("🔔 Decisão de som:", {
        shouldPlaySound,
        isOwnMessage,
        isActiveSession,
        modalState: activeSessionRef.current ? "aberto" : "fechado",
      });

      if (shouldPlaySound) {
        console.log(
          "🔊 Reproduzindo som de notificação - mensagem de outro usuário " +
            (activeSessionRef.current
              ? "em sessão diferente"
              : "(modal fechado)"),
        );
        playNotificationSoundRef.current?.();

        // Mostrar notificação toast se o modal estiver fechado
        // E apenas após inicialização completa
        console.log("🔍 Verificando se deve mostrar toast:", {
          modalFechado: !activeSessionRef.current,
          inicializado: isInitialized.current,
          temCallbackState: !!openChatModal,
          temCallbackRef: !!openChatModalRef.current,
        });

        if (!activeSessionRef.current && isInitialized.current) {
          const messageSession = sessionsRef.current.find(
            (s) => s.id === message.session_id,
          );

          console.log("🔍 Sessão encontrada para toast:", {
            found: !!messageSession,
            sessionId: messageSession?.id,
            sessionName: messageSession?.session_name,
            sessionType: messageSession?.session_type,
          });

          if (messageSession) {
            console.log(
              "🎉 MOSTRANDO NOTIFICAÇÃO TOAST para sessão:",
              messageSession.id,
            );
            try {
              toast(
                <ChatNotification
                  message={message}
                  session={messageSession}
                  onOpenChat={(session) => {
                    console.log("🖱️ Clicou na notificação, abrindo chat");
                    toast.dismiss();
                    // Usa requestOpenChat ao invés de callback direto
                    setPendingSessionToOpen(session);
                  }}
                />,
                {
                  progress: undefined,
                  hideProgressBar: true,
                  position: toast.POSITION.BOTTOM_RIGHT,
                  autoClose: 5000,
                  closeButton: true,
                  style: {
                    cursor: "pointer",
                  },
                },
              );
              console.log("✅ Toast criado com sucesso");
            } catch (error) {
              console.error("❌ Erro ao criar toast:", error);
            }
          } else {
            console.log("⚠️ Não pode mostrar toast - sessão não encontrada");
          }
        } else {
          console.log("⏭️ Toast não mostrado:", {
            razao: activeSessionRef.current
              ? "modal aberto"
              : "não inicializado",
          });
        }
      } else if (isOwnMessage) {
        console.log("👤 Mensagem do próprio usuário, não reproduzindo som");
      } else {
        console.log("👁️ Mensagem na sessão ativa, não reproduzindo som");
      }
    };

    // Limpar listeners anteriores antes de registrar novos
    socket.off("new_chat_message");
    console.log(
      "   Listeners APÓS .off():",
      socket.listeners("new_chat_message").length,
    );

    // Registrar event handler
    socket.on("new_chat_message", handleNewMessage);
    console.log("✅ Listener registrado!");
    console.log(
      "   Listeners APÓS .on():",
      socket.listeners("new_chat_message").length,
    );
    console.log("🔌 ==========================================");

    // ============= LISTENERS DE CHAMADA DE VÍDEO =============
    socket.off("incoming_video_call");
    socket.off("video_call_accepted");
    socket.off("video_call_rejected");
    socket.off("video_call_participant_joined");
    socket.off("video_call_participant_left");
    socket.off("video_call_signal");
    socket.off("video_call_ended");

    // Chamada de vídeo recebida
    socket.on("incoming_video_call", (data) => {
      console.log("📞 Chamada de vídeo recebida de:", data.caller.nome);

      const callerIdNum =
        typeof data.caller.id === "number"
          ? data.caller.id
          : parseInt(data.caller.id);
      const currentUserIdNum = currentUser?.id;

      if (currentUser && callerIdNum !== currentUserIdNum) {
        setVideoCallState({
          status: "incoming" as const,
          callId: data.callId,
          sessionId: data.sessionId,
          participants: [],
          initiator: data.caller,
        });
      }
    });

    // Chamada aceita
    socket.on("video_call_accepted", async (data) => {
      console.log("✅ ========================================");
      console.log("✅ VIDEO_CALL_ACCEPTED RECEBIDO!");
      console.log("   Call ID:", data.callId);
      console.log("   Participantes:", data.participants);
      console.log("   Current user ID:", currentUser?.id);
      console.log("✅ ========================================");

      try {
        if (!localStreamRef.current) {
          const preferredAudio = localStorage.getItem("preferredAudioDevice");
          const preferredVideo = localStorage.getItem("preferredVideoDevice");
          const constraints = getVideoCallConstraints(preferredAudio, preferredVideo);
          let stream: MediaStream;
          try {
            stream = await navigator.mediaDevices.getUserMedia(constraints);
          } catch {
            stream = await navigator.mediaDevices.getUserMedia({
              video: VIDEO_CALL_VIDEO,
              audio: VIDEO_CALL_AUDIO,
            });
          }
          localStreamRef.current = stream;
        }

        setVideoCallState((prev) => {
          if (!prev) return null;

          console.log("🎯 Configurando conexões peer:", {
            currentUserId: currentUser?.id,
            existingConnections: Array.from(peerConnectionsRef.current.keys()),
            newParticipants: data.participants.map((p: ChatUser) => p.id),
          });

          // Para cada participante na lista
          data.participants.forEach((participant: ChatUser) => {
            if (participant.id !== currentUser?.id) {
              // Verificar se já existe conexão peer com este participante
              const alreadyConnected = peerConnectionsRef.current.has(participant.id);
              
              if (!alreadyConnected) {
                // Determinar quem deve ser o initiator:
                // - O usuário com ID menor deve ser o initiator (para evitar conflitos)
                const shouldBeInitiator = (currentUser?.id || 0) < participant.id;
                
                console.log(`🔗 Criando NOVA peer connection com usuário ${participant.id}:`, {
                  shouldBeInitiator,
                  currentUserId: currentUser?.id,
                  participantId: participant.id,
                });
                
                createPeerConnectionRef.current?.(
                  participant.id,
                  data.callId,
                  shouldBeInitiator,
                );
              } else {
                console.log(`✅ Peer connection já existe com usuário ${participant.id}`);
              }
            }
          });

          console.log("📊 Estado final do videoCallState:", {
            status: "active",
            participantsCount: data.participants.length,
            participantIds: data.participants.map((p: ChatUser) => p.id),
            currentUserId: currentUser?.id,
          });

          // Forçar re-render do componente de vídeo
          setRemoteStreamsVersion((v) => v + 1);

          return {
            ...prev,
            status: "active" as const,
            participants: data.participants,
          };
        });
      } catch (error) {
        console.error("Erro ao iniciar chamada:", error);
        alert(
          "Não foi possível acessar sua câmera e microfone. Verifique as permissões.",
        );
      }
    });

    socket.on("video_call_rejected", (data) => {
      console.log("🚫 ========================================");
      console.log("🚫 video_call_rejected recebido:");
      console.log("   Call ID:", data.callId);
      console.log("   Usuário que recusou:", data.userId);
      console.log("   Participantes restantes:", data.remainingParticipants);
      console.log("🚫 ========================================");
      
      // Se ainda houver 2 ou mais participantes, manter a chamada ativa
      if (data.remainingParticipants >= 2) {
        console.log("✅ Chamada continua ativa com outros participantes");
        // Apenas mostrar notificação de que alguém recusou
        toast.info(`Um usuário recusou a chamada, mas a conversa continua.`, {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        console.log("❌ Encerrando chamada (menos de 2 participantes)");
        setVideoCallState(null);
        alert("A chamada foi recusada.");
      }
    });

    // Evento video_call_participant_joined não é mais necessário
    // pois video_call_accepted já gerencia todas as conexões

    socket.on("video_call_participant_left", (data) => {
      const peer = peerConnectionsRef.current.get(data.userId);
      if (peer) {
        peer.destroy();
        peerConnectionsRef.current.delete(data.userId);
      }
      remoteStreamsRef.current.delete(data.userId);
      setVideoCallState((prev) => (prev ? { ...prev } : null));
    });

    socket.on("video_call_signal", (data) => {
      console.log("📥 ========================================");
      console.log(`📥 Sinal recebido do usuário ${data.from}`);
      console.log("   Signal type:", data.signal?.type);
      console.log("   Para usuário:", data.to);

      const peer = peerConnectionsRef.current.get(data.from);
      console.log("   Peer encontrado?", !!peer);

      if (peer) {
        // @ts-ignore - Acessar propriedade interna do SimplePeer para verificar estado
        const isDestroyed = peer.destroyed;

        if (isDestroyed) {
          console.warn("   ⚠️ Peer já foi destruído, ignorando sinal");
          return;
        }

        console.log("   ✅ Aplicando sinal ao peer...");
        try {
          peer.signal(data.signal);
          console.log("   ✅ Sinal aplicado com sucesso!");
        } catch (error: any) {
          // Ignorar erro de "already have a remote description" - isso é normal em alguns casos
          if (
            error?.message?.includes("already have") ||
            error?.message?.includes("stable")
          ) {
            console.warn(
              "   ⚠️ Sinal ignorado - peer já está em estado estável",
            );
          } else {
            console.error("   ❌ Erro ao aplicar sinal:", error);
          }
        }
      } else {
        console.warn(
          "   ⚠️ Peer não encontrado! Conexão ainda não foi criada.",
        );
      }
      console.log("📥 ========================================");
    });

    socket.on("video_call_ended", (data) => {
      endVideoCallRef.current?.();
    });

    socket.on("video_call_participant_left", ({ userId, callId, remainingParticipants }) => {
      console.log(`👋 Participante ${userId} saiu da chamada ${callId}`);
      console.log(`📊 Participantes restantes: ${remainingParticipants}`);
      
      // Remover a conexão peer do participante que saiu
      const peer = peerConnectionsRef.current.get(userId);
      if (peer) {
        peer.destroy();
        peerConnectionsRef.current.delete(userId);
        console.log(`🗑️ Conexão peer removida do usuário ${userId}`);
      }
      
      // Remover o stream remoto
      if (remoteStreamsRef.current.has(userId)) {
        remoteStreamsRef.current.delete(userId);
        setRemoteStreamsVersion((v) => v + 1); // Forçar re-render
        console.log(`🗑️ Stream remoto removido do usuário ${userId}`);
      }
      
      // Atualizar lista de participantes no estado
      setVideoCallState((prev) => {
        if (prev) {
          return {
            ...prev,
            participants: prev.participants.filter((p) => p.id !== userId),
          };
        }
        return prev;
      });
    });

    // ============= FIM DOS LISTENERS DE VÍDEO =============

    return () => {
      socket.off("new_chat_message", handleNewMessage);
      socket.off("incoming_video_call");
      socket.off("video_call_accepted");
      socket.off("video_call_rejected");
      socket.off("video_call_participant_joined");
      socket.off("video_call_participant_left");
      socket.off("video_call_signal");
      socket.off("video_call_ended");
    };
  }, [socket, currentUser]); // ✅ Removidas dependências de funções - usando refs

  // Carregar sessões do usuário
  const loadUserSessions = useCallback(async () => {
    if (!currentUser) return;

    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL + "api/chat/sessions";
      const response = await api(apiUrl);

      if (response.ok) {
        const data = await response.json();
        console.log("📋 Sessões carregadas:", {
          total: data.length,
          private: data.filter((s: any) => s.session_type === "private").length,
          group: data.filter((s: any) => s.session_type === "group").length,
          external: data.filter((s: any) => s.session_type === "external")
            .length,
          totalUnread: data.reduce(
            (sum: number, s: any) => sum + (s.unread_count ?? 0),
            0,
          ),
        });
        setSessions(data);
      } else {
        setError("Erro ao carregar sessões");
      }
    } catch (err) {
      setError("Erro de conexão");
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Atualizar ref de loadUserSessions
  useEffect(() => {
    loadUserSessionsRef.current = loadUserSessions;
  }, [loadUserSessions]);

  // Teste de toast quando inicializa
  useEffect(() => {
    if (isInitialized.current && currentUser) {
      console.log(
        "🧪 ChatProvider totalmente inicializado e pronto para notificações",
      );
      // Teste simples de toast (comentar depois)
      // toast.info("ChatProvider inicializado!", { position: toast.POSITION.BOTTOM_RIGHT });
    }
  }, [currentUser]);

  // Carregar mensagens de uma sessão
  const loadMessages = useCallback(async (sessionId: number, page = 1) => {
    setLoading(true);
    try {
      const apiUrl =
        process.env.REACT_APP_API_URL +
        `api/chat/sessions/${sessionId}/messages?page=${page}&limit=50`;
      const response = await api(apiUrl);

      if (response.ok) {
        const data = await response.json();
        
        // Parsear attachments se vier como string
        const messagesWithParsedAttachments = data.map((msg: any) => {
          if (msg.attachments && typeof msg.attachments === 'string') {
            try {
              msg.attachments = JSON.parse(msg.attachments);
            } catch (e) {
              console.error('Erro ao parsear attachments:', e);
              msg.attachments = null;
            }
          }
          return msg;
        });
        
        if (page === 1) {
          setMessages(messagesWithParsedAttachments);
        } else {
          setMessages((prev) => [...messagesWithParsedAttachments, ...prev]);
        }
      } else {
        setError("Erro ao carregar mensagens");
      }
    } catch (err) {
      setError("Erro de conexão");
    } finally {
      setLoading(false);
    }
  }, []);

  // Enviar mensagem
  const sendMessage = useCallback(
    async (
      content: string,
      options?: {
        type?: ChatMessage["message_type"];
        file?: File;
        attachments?: any[];
      },
    ) => {
      if (!activeSession || !currentUser) return;

      const type = options?.type || "text";
      const file = options?.file;
      const attachments = options?.attachments;

      // Adicionar mensagem temporária imediatamente (optimistic update)
      const tempId = Date.now(); // ID temporário (timestamp)
      const tempMessage: ChatMessage = {
        id: tempId,
        session_id: activeSession.id,
        sender_id: currentUser.id,
        sender_name: currentUser.nome || currentUser.nomecomp,
        message_content: content,
        message_type: type,
        sent_at: new Date().toISOString(),
        file_url: undefined,
        file_name: undefined,
        file_size: undefined,
        is_edited: 0,
        is_deleted: 0,
        reads: [],
        attachments: attachments,
      } as any;

      console.log("📤 Enviando mensagem com ID temporário:", tempId);
      setMessages((prev) => {
        console.log("➕ Adicionando mensagem temporária à lista");
        return [...prev, tempMessage];
      });

      // Se for sessão externa (WhatsApp), enviar via API do WhatsApp
      if (activeSession.session_type === "external") {
        try {
          const apiUrl = process.env.REACT_APP_API_URL + "api/whatsapp/send";
          const response = await api(apiUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              session_id: activeSession.id,
              message: content,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error("Erro ao enviar mensagem WhatsApp:", errorData);
            // Remover mensagem temporária em caso de erro
            setMessages((prev) => prev.filter((m) => m.id !== tempMessage.id));
            throw new Error(errorData.error || "Erro ao enviar mensagem");
          }

          const data = await response.json();
          console.log("Mensagem WhatsApp enviada:", data);
          if (loadUserSessionsRef.current) {
            loadUserSessionsRef.current();
          }
        } catch (error) {
          console.error("Erro ao enviar mensagem externa:", error);
          throw error;
        }
        return;
      }

      // Para sessões internas, usar WebSocket
      if (!socket) {
        console.error("Socket não conectado");
        setMessages((prev) => prev.filter((m) => m.id !== tempMessage.id));
        setError("Erro de conexão. Tente novamente.");
        return;
      }

      if (!socket.connected) {
        console.error("Socket desconectado");
        setMessages((prev) => prev.filter((m) => m.id !== tempMessage.id));
        setError("Conexão perdida. Reconectando...");
        return;
      }

      // Garantir que o usuário está registrado no WebSocket
      const userId = localStorage.getItem("rh-userId");
      const userName = localStorage.getItem("rh-userName");

      if (userId) {
        console.log("Re-registrando usuário antes de enviar mensagem");
        socket.emit("newUser", userId, userName, userId);
      }

      const messageData = {
        session_id: activeSession.id,
        message_content: content,
        message_type: type,
        file_url: null,
        file_name: null,
        file_size: null,
        attachments: attachments,
      };

      // Se houver arquivo, fazer upload primeiro
      if (file) {
        // Implementar upload de arquivo
        // messageData.file_url = uploadedFileUrl;
        // messageData.file_name = file.name;
        // messageData.file_size = file.size;
      }

      console.log("📡 Enviando mensagem via WebSocket:", {
        tempId,
        sessionId: activeSession.id,
        content: content.substring(0, 50) + (content.length > 50 ? "..." : ""),
        socketConnected: socket.connected,
      });

      socket.emit("send_chat_message", messageData);
    },
    [activeSession, socket, currentUser],
  );

  // Criar nova sessão
  const createSession = useCallback(
    async (
      participants: number[],
      sessionName?: string,
      sessionType = "private" as "private" | "group",
    ) => {
      if (!currentUser) {
        console.error("CurrentUser não encontrado:", currentUser);
        console.error("LocalStorage user:", localStorage.getItem("user"));
        throw new Error("Usuário não logado");
      }

      console.log("Criando sessão com usuário:", currentUser);
      setLoading(true);
      try {
        const apiUrl = process.env.REACT_APP_API_URL + "api/chat/sessions";
        const response = await api(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            session_name: sessionName,
            session_type: sessionType,
            participant_ids: participants,
          }),
        });

        if (response.ok) {
          const newSession = await response.json();
          setSessions((prev) => [newSession, ...prev]);
          return newSession;
        } else {
          throw new Error("Erro ao criar sessão");
        }
      } catch (err) {
        setError("Erro ao criar sessão");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [currentUser],
  );

  // Marcar mensagem como lida
  const markAsRead = useCallback(
    async (messageId: number) => {
      if (!socket) return;

      socket.emit("mark_message_read", messageId);
    },
    [socket],
  );

  // Marcar todas as mensagens de uma sessão como lidas
  const markSessionAsRead = useCallback(async (sessionId: number) => {
    try {
      console.log("🔵 Marcando sessão como lida:", sessionId);
      const apiUrl =
        process.env.REACT_APP_API_URL + `api/chat/sessions/${sessionId}/read`;
      console.log("🔵 URL da requisição:", apiUrl);
      const response = await api(apiUrl, {
        method: "PUT",
      });

      console.log("🔵 Resposta da API:", {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
      });

      if (response.ok) {
        console.log("✅ Sessão marcada como lida com sucesso:", sessionId);

        // Atualizar o unread_count da sessão para 0 no estado local
        setSessions((prev) => {
          const updated = prev.map((session) =>
            session.id === sessionId
              ? { ...session, unread_count: 0 }
              : session,
          );
          console.log("✅ Estado local atualizado. Contador zerado.");
          return updated;
        });
      } else {
        const errorText = await response.text();
        console.error("❌ Erro ao marcar sessão como lida:", {
          status: response.status,
          error: errorText,
        });
      }
    } catch (error) {
      console.error("❌ Exceção ao marcar sessão como lida:", error);
    }
  }, []);

  // Atualizar status de digitação
  const updateTypingStatus = useCallback(
    (sessionId: number, isTyping: boolean) => {
      if (!socket) return;

      if (isTyping) {
        socket.emit("typing_start", { sessionId });
      } else {
        socket.emit("typing_stop", { sessionId });
      }
    },
    [socket],
  );

  // Buscar sessões
  const searchSessions = useCallback(
    (query: string) => {
      return sessions.filter(
        (session) =>
          session.session_name?.toLowerCase().includes(query.toLowerCase()) ||
          session.participants?.some(
            (p) =>
              p.user?.nome?.toLowerCase().includes(query.toLowerCase()) ||
              p.user?.nomecomp?.toLowerCase().includes(query.toLowerCase()),
          ),
      );
    },
    [sessions],
  );

  // Buscar mensagens
  const searchMessages = useCallback(
    (query: string, sessionId?: number) => {
      let messagesToSearch = messages;
      if (sessionId) {
        messagesToSearch = messages.filter((m) => m.session_id === sessionId);
      }

      return messagesToSearch.filter((message) =>
        message.message_content.toLowerCase().includes(query.toLowerCase()),
      );
    },
    [messages],
  );

  // Entrar em uma sessão
  const handleSetActiveSession = useCallback(
    (session: ChatSession | null) => {
      // Sair da sessão anterior
      if (activeSession && socket) {
        socket.emit("leave_chat_session", activeSession.id);
      }

      // Entrar na nova sessão
      if (session && socket) {
        console.log("Entrando na sessão de chat:", session.id);
        console.log("Socket conectado:", socket.connected);
        socket.emit("join_chat_session", session.id);
      } else {
        console.log("Não foi possível entrar na sessão:", {
          session: !!session,
          socket: !!socket,
        });
      }

      setActiveSession(session);
    },
    [activeSession, socket],
  );

  // Carregar sessões quando o usuário for definido
  useEffect(() => {
    if (currentUser) {
      loadUserSessions().then(() => {
        // Marcar como inicializado após primeira carga
        setTimeout(() => {
          isInitialized.current = true;
          console.log("✅ Chat Provider inicializado");
        }, 1000);
      });

      // Recarregar sessões periodicamente para manter contadores sincronizados
      const interval = setInterval(() => {
        console.log("🔄 Recarregando sessões (automático)");
        loadUserSessions();
      }, 30000); // A cada 30 segundos

      return () => clearInterval(interval);
    }
  }, [currentUser, loadUserSessions]);

  // Recarregar mensagens quando a sessão ativa muda
  useEffect(() => {
    if (activeSession) {
      console.log(
        "Sessão ativa mudou, recarregando mensagens:",
        activeSession.id,
      );
      loadMessages(activeSession.id);

      // Marcar todas as mensagens da sessão como lidas
      markSessionAsRead(activeSession.id);
    } else {
      // Limpar mensagens quando não há sessão ativa
      setMessages([]);
    }
  }, [activeSession, loadMessages, markSessionAsRead]);

  // Limpar erro
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Função para solicitar abertura do chat (independente de callback)
  const requestOpenChat = useCallback((session: ChatSession) => {
    console.log(
      "🎯 ChatProvider: requestOpenChat chamado para sessão:",
      session.id,
    );
    setPendingSessionToOpen(session);
  }, []);

  // Limpar pending session após um tempo (evitar loops)
  useEffect(() => {
    if (pendingSessionToOpen) {
      const timeout = setTimeout(() => {
        console.log("🧹 Limpando pendingSessionToOpen");
        setPendingSessionToOpen(null);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [pendingSessionToOpen]);

  // Monitorar mudanças no estado da chamada de vídeo
  useEffect(() => {
    if (videoCallState) {
      console.log("📞 ========================================");
      console.log("📞 VIDEO CALL STATE MUDOU!");
      console.log("   Status:", videoCallState.status);
      console.log("   Call ID:", videoCallState.callId);
      console.log("   Session ID:", videoCallState.sessionId);
      console.log("   Iniciador:", videoCallState.initiator);
      console.log("📞 ========================================");
    }
  }, [videoCallState]);

  // Calcular total de mensagens não lidas
  const totalUnreadCount = useMemo(() => {
    return sessions.reduce((total, session) => {
      return total + (session.unread_count ?? 0);
    }, 0);
  }, [sessions]);

  const value = {
    currentUser,
    sessions,
    activeSession,
    messages,
    onlineUsers,
    loading,
    error,
    totalUnreadCount,
    openChatModal,
    pendingSessionToOpen,
    videoCallState,
    localStreamRef,
    remoteStreamsRef,
    setActiveSession: handleSetActiveSession,
    setOpenChatModal: setOpenChatModalWithLogs,
    requestOpenChat,
    sendMessage,
    createSession,
    loadMessages,
    markAsRead,
    updateTypingStatus,
    searchSessions,
    searchMessages,
    loadUserSessions,
    clearError,
    startVideoCall,
    acceptVideoCall,
    rejectVideoCall,
    endVideoCall,
    toggleAudio,
    toggleVideo,
    changeAudioDevice,
    changeVideoDevice,
  } as ChatContextType;

  return (
    <ChatContext.Provider value={value}>
      {children}

      {/* Modais de chamada de vídeo - renderizados globalmente */}
      {videoCallState?.status === "calling" && (
        <ModalVideoCalling
          open={true}
          session={
            sessions.find((s) => s.id === videoCallState.sessionId) || null
          }
          onCancel={endVideoCall}
        />
      )}

      {videoCallState?.status === "incoming" &&
        (() => {
          const initiatorIdNum =
            typeof videoCallState.initiator?.id === "number"
              ? videoCallState.initiator.id
              : parseInt(videoCallState.initiator?.id || "0");
          const currentUserIdNum = currentUser?.id;

          if (initiatorIdNum !== currentUserIdNum) {
            return (
              <ModalVideoIncoming
                open={true}
                caller={videoCallState.initiator}
                onAccept={() => acceptVideoCall(videoCallState.callId!)}
                onReject={() => rejectVideoCall(videoCallState.callId!)}
              />
            );
          }
          return null;
        })()}

      {videoCallState?.status === "active" &&
        (() => {
          console.log("🎬 Renderizando ModalVideoCall:", {
            hasLocalStream: !!localStreamRef.current,
            localVideoTracks: localStreamRef.current?.getVideoTracks().length,
            localAudioTracks: localStreamRef.current?.getAudioTracks().length,
            remoteStreamsCount: remoteStreamsRef.current?.size || 0,
            participantsCount: videoCallState.participants.length,
            participants: videoCallState.participants.map((p) => ({
              id: p.id,
              nome: p.nome,
            })),
          });

          return (
            <ModalVideoCall
              key={`video-call-${remoteStreamsVersion}`}
              open={true}
              localStream={localStreamRef.current}
              remoteStreams={remoteStreamsRef.current || new Map()}
              participants={videoCallState.participants}
              onToggleAudio={toggleAudio}
              onToggleVideo={toggleVideo}
              onEndCall={endVideoCall}
              onChangeAudioDevice={changeAudioDevice}
              onChangeVideoDevice={changeVideoDevice}
            />
          );
        })()}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};
